import React, { useState, useEffect, useRef } from 'react';

const AboutBentley = ({
    videoSrc,
    logoSrc,
    logoSmallSrc
}) => {
    const [isOpened, setIsOpened] = useState(false);
    const [isVisible, setIsVisible] = useState(false);
    const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
    const [cardHovered, setCardHovered] = useState(false);
    const [tilt, setTilt] = useState({ x: 0, y: 0 });
    const sectionRef = useRef(null);
    const cardRef = useRef(null);

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) setIsVisible(true);
                });
            },
            { threshold: 0.15 }
        );
        if (sectionRef.current) observer.observe(sectionRef.current);
        return () => { if (sectionRef.current) observer.unobserve(sectionRef.current); };
    }, []);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (!e.target.closest('.flip-card-root')) setIsOpened(false);
        };
        if (isOpened) document.addEventListener('click', handleClickOutside);
        return () => document.removeEventListener('click', handleClickOutside);
    }, [isOpened]);

    const handleMouseMove = (e) => {
        if (!cardRef.current || isOpened) return;
        const rect = cardRef.current.getBoundingClientRect();
        const x = (e.clientX - rect.left) / rect.width;
        const y = (e.clientY - rect.top) / rect.height;
        setTilt({
            x: (y - 0.5) * 18,
            y: -(x - 0.5) * 18,
        });
        setMousePos({ x, y });
    };

    const handleMouseLeave = () => {
        setTilt({ x: 0, y: 0 });
        setCardHovered(false);
    };

    const styles = `
        @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700;800;900&family=Plus+Jakarta+Sans:ital,wght@0,300;0,400;0,500;0,600;0,700;0,800;1,300;1,400&display=swap');

        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

        :root {
            --teal: #356575;
            --teal-dark: #2a4f5e;
            --teal-deep: #1e3a46;
            --teal-light: rgba(53,101,117,0.12);
            --teal-glow: rgba(53,101,117,0.35);
            --white: #ffffff;
            --off-white: #f7f9fb;
            --text-dark: #18262d;
            --text-mid: #3d5a65;
            --text-muted: #7a9ba6;
            --border: rgba(53,101,117,0.18);
        }

        .adk-section {
            position: relative;
            padding: 7rem 0;
            background: var(--white);
            overflow: hidden;
            font-family: 'Plus Jakarta Sans', sans-serif;
        }

        /* ── Mesh background ── */
        .adk-mesh {
            position: absolute;
            inset: 0;
            pointer-events: none;
            overflow: hidden;
        }
        .adk-mesh::before {
            content: '';
            position: absolute;
            width: 700px; height: 700px;
            border-radius: 50%;
            background: radial-gradient(circle, rgba(53,101,117,0.09) 0%, transparent 65%);
            top: -200px; left: -150px;
            animation: driftA 22s ease-in-out infinite;
        }
        .adk-mesh::after {
            content: '';
            position: absolute;
            width: 500px; height: 500px;
            border-radius: 50%;
            background: radial-gradient(circle, rgba(53,101,117,0.06) 0%, transparent 65%);
            bottom: -150px; right: -100px;
            animation: driftB 18s ease-in-out infinite;
        }
        @keyframes driftA {
            0%,100% { transform: translate(0,0) scale(1); }
            50% { transform: translate(60px,-40px) scale(1.12); }
        }
        @keyframes driftB {
            0%,100% { transform: translate(0,0) scale(1); }
            50% { transform: translate(-40px,30px) scale(1.08); }
        }

        /* Grid lines */
        .adk-grid-lines {
            position: absolute;
            inset: 0;
            background-image:
                linear-gradient(rgba(53,101,117,0.04) 1px, transparent 1px),
                linear-gradient(90deg, rgba(53,101,117,0.04) 1px, transparent 1px);
            background-size: 60px 60px;
            pointer-events: none;
            mask-image: radial-gradient(ellipse 80% 80% at 50% 50%, black 30%, transparent 100%);
        }

        .adk-container {
            max-width: 1320px;
            margin: 0 auto;
            padding: 0 2rem;
            position: relative;
            z-index: 2;
        }

        .adk-grid {
            display: grid;
            grid-template-columns: 1.1fr 0.9fr;
            gap: 5rem;
            align-items: center;
        }

        /* ── LEFT CONTENT ── */
        .adk-content {
            opacity: 0;
            transform: translateX(-40px);
            transition: opacity 0.9s cubic-bezier(0.4,0,0.2,1), transform 0.9s cubic-bezier(0.4,0,0.2,1);
        }
        .adk-section.visible .adk-content {
            opacity: 1;
            transform: translateX(0);
        }

        .adk-badge {
            display: inline-flex;
            align-items: center;
            gap: 0.5rem;
            padding: 0.45rem 1.1rem;
            background: var(--teal-light);
            border: 1px solid var(--border);
            border-radius: 100px;
            font-size: 0.8rem;
            font-weight: 600;
            color: var(--teal);
            letter-spacing: 0.06em;
            text-transform: uppercase;
            margin-bottom: 1.75rem;
            transition: background 0.3s, transform 0.3s;
        }
        .adk-badge:hover { background: rgba(53,101,117,0.18); transform: translateX(4px); }
        .adk-badge-dot {
            width: 6px; height: 6px;
            border-radius: 50%;
            background: var(--teal);
            animation: pulse-dot 2s ease-in-out infinite;
        }
        @keyframes pulse-dot {
            0%,100% { opacity: 1; transform: scale(1); }
            50% { opacity: 0.5; transform: scale(1.5); }
        }

        .adk-title {
            font-family: 'Outfit', sans-serif;
            font-size: 2.4rem;
            font-weight: 700;
            line-height: 1.1;
            letter-spacing: -0.02em;
            color: #29354d;
            margin-bottom: 0.5rem;
        }
        .adk-title-line {
            display: block;
            overflow: hidden;
        }
        .adk-title-inner {
            display: block;
            transform: translateY(110%);
            transition: transform 1s cubic-bezier(0.4,0,0.2,1);
        }
        .adk-section.visible .adk-title-inner { transform: translateY(0); }
        .adk-section.visible .adk-title-line:nth-child(2) .adk-title-inner { transition-delay: 0.12s; }

        .adk-title-accent {
            color: var(--teal);
            position: relative;
        }
        .adk-title-accent::after {
            content: '';
            position: absolute;
            bottom: 2px; left: 0;
            width: 100%; height: 3px;
            background: linear-gradient(90deg, var(--teal), transparent);
            border-radius: 2px;
            transform: scaleX(0);
            transform-origin: left;
            transition: transform 1.2s cubic-bezier(0.4,0,0.2,1) 0.8s;
        }
        .adk-section.visible .adk-title-accent::after { transform: scaleX(1); }

        .adk-divider {
            width: 60px; height: 2px;
            background: linear-gradient(90deg, var(--teal), transparent);
            border-radius: 2px;
            margin: 1.75rem 0;
            transform: scaleX(0);
            transform-origin: left;
            transition: transform 1s cubic-bezier(0.4,0,0.2,1) 0.5s;
        }
        .adk-section.visible .adk-divider { transform: scaleX(1); }

        .adk-desc {
            font-size: 1rem;
            line-height: 1.7;
            color: #6b7280;
            margin-bottom: 1rem;
            opacity: 0;
            transform: translateY(16px);
            transition: opacity 0.7s ease, transform 0.7s ease;
        }
        .adk-desc:last-of-type { margin-bottom: 2.5rem; }
        .adk-section.visible .adk-desc { opacity: 1; transform: translateY(0); }
        .adk-section.visible .adk-desc:nth-of-type(1) { transition-delay: 0.4s; }
        .adk-section.visible .adk-desc:nth-of-type(2) { transition-delay: 0.55s; }

        /* Stats */
        .adk-stats {
            display: flex;
            gap: 0;
            background: var(--off-white);
            border: 1px solid var(--border);
            border-radius: 16px;
            overflow: hidden;
            opacity: 0;
            transform: translateY(20px);
            transition: opacity 0.7s ease 0.7s, transform 0.7s ease 0.7s;
        }
        .adk-section.visible .adk-stats { opacity: 1; transform: translateY(0); }

        .adk-stat {
            flex: 1;
            padding: 1.5rem 1.25rem;
            text-align: center;
            position: relative;
            transition: background 0.3s;
        }
        .adk-stat:hover { background: rgba(53,101,117,0.05); }
        .adk-stat + .adk-stat::before {
            content: '';
            position: absolute;
            left: 0; top: 20%; bottom: 20%;
            width: 1px;
            background: var(--border);
        }

        .adk-stat-num {
            display: block;
            font-family: 'Outfit', sans-serif;
            font-size: 1.85rem;
            font-weight: 800;
            color: var(--teal);
            line-height: 1;
            margin-bottom: 0.35rem;
        }
        .adk-stat-label {
            font-size: 0.78rem;
            font-weight: 500;
            color: var(--text-muted);
            text-transform: uppercase;
            letter-spacing: 0.07em;
        }

        /* ── RIGHT CARD WRAPPER ── */
        .adk-card-area {
            position: relative;
            opacity: 0;
            transform: translateX(40px);
            transition: opacity 0.9s cubic-bezier(0.4,0,0.2,1) 0.2s, transform 0.9s cubic-bezier(0.4,0,0.2,1) 0.2s;
            display: flex;
            justify-content: center;
        }
        .adk-section.visible .adk-card-area {
            opacity: 1;
            transform: translateX(0);
        }

        /* Orbital rings behind card */
        .adk-ring {
            position: absolute;
            border-radius: 50%;
            border: 1px solid var(--border);
            pointer-events: none;
            animation: spinRing linear infinite;
        }
        .adk-ring-1 {
            width: 340px; height: 340px;
            top: 50%; left: 50%;
            transform: translate(-50%, -50%);
            animation-duration: 30s;
        }
        .adk-ring-2 {
            width: 420px; height: 420px;
            top: 50%; left: 50%;
            transform: translate(-50%, -50%);
            animation-duration: 45s;
            animation-direction: reverse;
            opacity: 0.5;
        }
        @keyframes spinRing {
            0% { transform: translate(-50%, -50%) rotate(0deg); }
            100% { transform: translate(-50%, -50%) rotate(360deg); }
        }

        /* Ring dots */
        .adk-ring-1::before, .adk-ring-2::after {
            content: '';
            position: absolute;
            width: 8px; height: 8px;
            border-radius: 50%;
            background: var(--teal);
            top: -4px; left: 50%;
            transform: translateX(-50%);
        }
        .adk-ring-2::after {
            background: rgba(53,101,117,0.5);
            width: 6px; height: 6px;
            top: auto; bottom: -3px;
        }

        /* ── FLIP CARD ── */
        .flip-card-root {
            position: relative;
            width: 340px;
            height: 440px;
            perspective: 1400px;
            cursor: pointer;
            z-index: 2;
        }

        .flip-card-inner {
            position: relative;
            width: 100%;
            height: 100%;
            transform-style: preserve-3d;
            transition: transform 0.75s cubic-bezier(0.4, 0.2, 0.2, 1);
            will-change: transform;
        }
        .flip-card-root.flipped .flip-card-inner {
            transform: rotateY(180deg);
        }

        /* Tilt applied via inline style */
        .flip-card-inner.tilted {
            transition: transform 0.1s linear;
        }

        .card-face {
            position: absolute;
            inset: 0;
            backface-visibility: hidden;
            border-radius: 20px;
            overflow: hidden;
        }

        /* ── FRONT ── */
        .card-front {
            background: var(--white);
            border: 1.5px solid var(--border);
            box-shadow:
                0 4px 24px rgba(53,101,117,0.08),
                0 1px 3px rgba(53,101,117,0.06);
            display: flex;
            flex-direction: column;
            transition: box-shadow 0.4s, border-color 0.4s;
        }
        .flip-card-root:hover .card-front {
            border-color: rgba(53,101,117,0.45);
            box-shadow:
                0 20px 60px rgba(53,101,117,0.22),
                0 4px 16px rgba(53,101,117,0.1);
        }

        /* Video zone – reduced */
        .card-video-zone {
            position: relative;
            height: 55%;
            overflow: hidden;
            background: var(--teal-deep);
        }
        .card-video-el {
            width: 100%;
            height: 100%;
            object-fit: cover;
            opacity: 0.85;
            transition: transform 0.8s cubic-bezier(0.4,0,0.2,1), opacity 0.4s;
        }
        .flip-card-root:hover .card-video-el {
            transform: scale(1.08);
            opacity: 1;
        }

        /* Cinematic bars */
        .card-video-zone::before,
        .card-video-zone::after {
            content: '';
            position: absolute;
            left: 0; right: 0;
            height: 28px;
            background: var(--white);
            z-index: 2;
            transition: height 0.4s;
        }
        .card-video-zone::before { top: 0; }
        .card-video-zone::after  { bottom: 0; }
        .flip-card-root:hover .card-video-zone::before,
        .flip-card-root:hover .card-video-zone::after { height: 0; }

        /* Scan line effect */
        .card-scan {
            position: absolute;
            inset: 0;
            background: repeating-linear-gradient(
                0deg,
                transparent,
                transparent 3px,
                rgba(0,0,0,0.04) 3px,
                rgba(0,0,0,0.04) 4px
            );
            pointer-events: none;
            z-index: 3;
        }

        /* Video overlay gradient */
        .card-video-grad {
            position: absolute;
            inset: 0;
            background: linear-gradient(
                to bottom,
                transparent 0%,
                transparent 50%,
                rgba(255,255,255,0.6) 80%,
                var(--white) 100%
            );
            z-index: 4;
        }

        /* Corner accent */
        .card-corner-tl, .card-corner-br {
            position: absolute;
            width: 20px; height: 20px;
            z-index: 5;
        }
        .card-corner-tl {
            top: 10px; left: 10px;
            border-top: 2px solid var(--teal);
            border-left: 2px solid var(--teal);
        }
        .card-corner-br {
            bottom: 10px; right: 10px;
            border-bottom: 2px solid var(--teal);
            border-right: 2px solid var(--teal);
        }

        .card-front-body {
            flex: 1;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            padding: 1.5rem 1.75rem 1.25rem;
            gap: 0.75rem;
            text-align: center;
        }

        .card-logo-ring {
            position: relative;
            width: 68px; height: 68px;
        }
        .card-logo-ring::before {
            content: '';
            position: absolute;
            inset: -6px;
            border-radius: 50%;
            border: 1.5px dashed var(--border);
            animation: spinRing 12s linear infinite;
        }
        .card-logo-box {
            width: 100%; height: 100%;
            border-radius: 15px;
            background: var(--teal-light);
            border: 1.5px solid var(--border);
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 0.85rem;
            transition: all 0.45s cubic-bezier(0.4,0,0.2,1);
        }
        .flip-card-root:hover .card-logo-box {
            background: var(--teal);
            border-color: var(--teal);
            box-shadow: 0 0 0 8px rgba(53,101,117,0.12);
            transform: rotate(-8deg) scale(1.08);
        }
        .card-logo-img {
            width: 100%; height: 100%;
            object-fit: contain;
            filter: brightness(0) saturate(100%) invert(35%) sepia(26%) saturate(827%) hue-rotate(152deg) brightness(92%) contrast(88%);
            transition: filter 0.4s;
        }
        .flip-card-root:hover .card-logo-img {
            filter: brightness(0) invert(1);
        }

        .card-name {
            font-family: 'Outfit', sans-serif;
            font-size: 1.5rem;
            font-weight: 800;
            color: var(--text-dark);
            letter-spacing: -0.02em;
            line-height: 1;
            transition: color 0.3s;
        }
        .flip-card-root:hover .card-name { color: var(--teal); }

        .card-tagline {
            font-size: 0.82rem;
            color: var(--text-muted);
            font-weight: 400;
            letter-spacing: 0.02em;
        }

        .card-hint {
            display: flex;
            align-items: center;
            gap: 0.4rem;
            font-size: 0.78rem;
            font-weight: 500;
            color: var(--text-muted);
            margin-top: auto;
            padding-top: 0.5rem;
            border-top: 1px solid var(--border);
            width: 100%;
            justify-content: center;
            transition: color 0.3s, gap 0.3s;
        }
        .flip-card-root:hover .card-hint { color: var(--teal); gap: 0.65rem; }

        .hint-arrow {
            width: 16px; height: 16px;
            transition: transform 0.4s cubic-bezier(0.4,0,0.2,1);
        }
        .flip-card-root:hover .hint-arrow {
            animation: bounceY 0.7s ease infinite;
        }
        @keyframes bounceY {
            0%,100% { transform: translateY(0); }
            50% { transform: translateY(4px); }
        }

        /* Shimmer on hover */
        .card-shimmer {
            position: absolute;
            inset: 0;
            background: linear-gradient(
                105deg,
                transparent 30%,
                rgba(255,255,255,0.5) 50%,
                transparent 70%
            );
            transform: translateX(-100%);
            pointer-events: none;
            z-index: 10;
            transition: none;
        }
        .flip-card-root:hover .card-shimmer {
            animation: shimmerSlide 0.7s ease forwards;
        }
        @keyframes shimmerSlide {
            to { transform: translateX(100%); }
        }

        /* ── BACK ── */
        .card-back {
            transform: rotateY(180deg);
            background: linear-gradient(145deg, var(--teal-deep) 0%, var(--teal-dark) 100%);
            border: 1.5px solid var(--teal);
            box-shadow:
                0 0 0 1px rgba(53,101,117,0.5),
                0 20px 60px rgba(53,101,117,0.4);
            display: flex;
            flex-direction: column;
            padding: 1.75rem;
            color: var(--white);
        }

        /* Noise texture on back */
        .card-back::before {
            content: '';
            position: absolute;
            inset: 0;
            background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)' opacity='0.04'/%3E%3C/svg%3E");
            border-radius: inherit;
            pointer-events: none;
        }

        .back-header {
            display: flex;
            align-items: center;
            gap: 0.75rem;
            padding-bottom: 1rem;
            border-bottom: 1px solid rgba(255,255,255,0.12);
            margin-bottom: 1rem;
        }
        .back-logo-sm {
            width: 36px; height: 36px;
            border-radius: 9px;
            background: rgba(255,255,255,0.12);
            border: 1px solid rgba(255,255,255,0.2);
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 0.5rem;
            flex-shrink: 0;
        }
        .back-logo-sm img {
            width: 100%; height: 100%;
            object-fit: contain;
            filter: brightness(0) invert(1);
        }
        .back-title {
            font-family: 'Outfit', sans-serif;
            font-size: 1.25rem;
            font-weight: 800;
            letter-spacing: -0.02em;
        }
        .back-status {
            margin-left: auto;
            display: flex;
            align-items: center;
            gap: 0.4rem;
            font-size: 0.72rem;
            color: rgba(255,255,255,0.6);
            font-weight: 500;
        }
        .back-status-dot {
            width: 5px; height: 5px;
            border-radius: 50%;
            background: #5eead4;
            animation: pulse-dot 2s ease-in-out infinite;
        }

        .back-desc {
            font-size: 0.9rem;
            line-height: 1.7;
            color: rgba(255,255,255,0.72);
            margin-bottom: 1rem;
            font-weight: 300;
        }

        .back-features {
            display: flex;
            flex-direction: column;
            gap: 0.6rem;
            flex: 1;
        }
        .back-feat {
            display: flex;
            align-items: center;
            gap: 0.65rem;
            font-size: 0.875rem;
            font-weight: 500;
            color: rgba(255,255,255,0.88);
            opacity: 0;
            transform: translateX(-16px);
        }
        .flip-card-root.flipped .back-feat:nth-child(1) { animation: featIn 0.45s ease 0.25s forwards; }
        .flip-card-root.flipped .back-feat:nth-child(2) { animation: featIn 0.45s ease 0.35s forwards; }
        .flip-card-root.flipped .back-feat:nth-child(3) { animation: featIn 0.45s ease 0.45s forwards; }
        .flip-card-root.flipped .back-feat:nth-child(4) { animation: featIn 0.45s ease 0.55s forwards; }
        @keyframes featIn {
            to { opacity: 1; transform: translateX(0); }
        }
        .feat-check {
            width: 18px; height: 18px;
            border-radius: 5px;
            background: rgba(255,255,255,0.12);
            border: 1px solid rgba(255,255,255,0.2);
            display: flex;
            align-items: center;
            justify-content: center;
            flex-shrink: 0;
        }
        .feat-check svg { width: 10px; height: 10px; color: #5eead4; }

        .back-btns {
            display: flex;
            flex-direction: column;
            gap: 0.65rem;
            margin-top: 1.25rem;
        }
        .btn-primary {
            padding: 0.85rem;
            border-radius: 12px;
            font-family: 'Plus Jakarta Sans', sans-serif;
            font-size: 0.9rem;
            font-weight: 600;
            border: none;
            cursor: pointer;
            background: var(--white);
            color: var(--teal-deep);
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 0.5rem;
            transition: all 0.3s cubic-bezier(0.4,0,0.2,1);
            box-shadow: 0 4px 16px rgba(0,0,0,0.2);
        }
        .btn-primary:hover {
            transform: translateY(-2px);
            box-shadow: 0 8px 24px rgba(0,0,0,0.3);
        }
        .btn-primary svg { transition: transform 0.3s; }
        .btn-primary:hover svg { transform: translateX(4px); }

        .btn-secondary {
            padding: 0.85rem;
            border-radius: 12px;
            font-family: 'Plus Jakarta Sans', sans-serif;
            font-size: 0.9rem;
            font-weight: 600;
            border: 1px solid rgba(255,255,255,0.25);
            cursor: pointer;
            background: rgba(255,255,255,0.07);
            color: rgba(255,255,255,0.88);
            transition: all 0.3s ease;
            backdrop-filter: blur(4px);
        }
        .btn-secondary:hover {
            background: rgba(255,255,255,0.14);
            border-color: rgba(255,255,255,0.4);
            transform: translateY(-2px);
        }

        /* ── RESPONSIVE ── */
        @media (max-width: 1100px) {
            .adk-grid { gap: 3.5rem; }
        }
        @media (max-width: 920px) {
            .adk-grid { grid-template-columns: 1fr; gap: 4rem; }
            .adk-content { text-align: center; }
            .adk-divider { margin: 1.75rem auto; }
            .adk-title-accent::after { left: 50%; transform: translateX(-50%); }
            .adk-card-area { order: -1; }
        }
        @media (max-width: 540px) {
            .flip-card-root { width: 300px; height: 400px; }
            .adk-ring-1 { width: 290px; height: 290px; }
            .adk-ring-2 { width: 360px; height: 360px; }
            .adk-stats { flex-direction: column; }
            .adk-stat + .adk-stat::before { width: 100%; height: 1px; top: 0; left: 0; bottom: auto; }
        }

        @media (prefers-reduced-motion: reduce) {
            *, *::before, *::after {
                animation-duration: 0.01ms !important;
                transition-duration: 0.01ms !important;
            }
        }
    `;

    const tiltStyle = !isOpened && cardHovered
        ? {
            transform: `rotateX(${tilt.x}deg) rotateY(${tilt.y}deg)`,
            transition: 'transform 0.08s linear',
        }
        : {};

    return (
        <>
            <style>{styles}</style>
            <section
                ref={sectionRef}
                className={`adk-section${isVisible ? ' visible' : ''}`}
            >
                <div className="adk-mesh" />
                <div className="adk-grid-lines" />

                <div className="adk-container">
                    <div className="adk-grid">

                        {/* ── LEFT ── */}
                        <div className="adk-content">
                            <div className="adk-badge">
                                <span className="adk-badge-dot" />
                                Infrastructure Leader
                            </div>

                            <h2 className="adk-title">
                                <span className="adk-title-inner">
                                    About <span className="adk-title-accent">Bentley</span>
                                </span>
                            </h2>

                            <div className="adk-divider" />

                            <p className="adk-desc">
                                Bentley Systems is a global leader in infrastructure engineering software, empowering professionals to design, build, and operate the world's critical infrastructure.
                            </p>
                            <p className="adk-desc">
                                From roads and bridges to water networks and structural systems, Bentley's software helps engineers deliver more resilient, sustainable infrastructure projects around the world.
                            </p>

                            <div className="adk-stats">
                                {[
                                    { num: '190+', label: 'Countries' },
                                    { num: '40+', label: 'Years Legacy' },
                                    { num: '5,000+', label: 'Colleagues' },
                                ].map((s) => (
                                    <div className="adk-stat" key={s.label}>
                                        <span className="adk-stat-num">{s.num}</span>
                                        <span className="adk-stat-label">{s.label}</span>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* ── RIGHT ── */}
                        <div className="adk-card-area">
                            {/* Decorative rings */}
                            <div className="adk-ring adk-ring-1" />
                            <div className="adk-ring adk-ring-2" />

                            <div
                                ref={cardRef}
                                className={`flip-card-root${isOpened ? ' flipped' : ''}`}
                                onClick={() => setIsOpened(!isOpened)}
                                onMouseMove={handleMouseMove}
                                onMouseEnter={() => setCardHovered(true)}
                                onMouseLeave={handleMouseLeave}
                            >
                                <div
                                    className="flip-card-inner"
                                    style={tiltStyle}
                                >
                                    {/* ── FRONT ── */}
                                    <div className="card-face card-front">
                                        <div className="card-shimmer" />
                                        <div className="card-video-zone">
                                            <video
                                                className="card-video-el"
                                                autoPlay loop muted playsInline
                                            >
                                                <source src={videoSrc} type="video/mp4" />
                                            </video>
                                            <div className="card-scan" />
                                            <div className="card-video-grad" />
                                            <div className="card-corner-tl" />
                                            <div className="card-corner-br" />
                                        </div>

                                        <div className="card-front-body">
                                            <div className="card-logo-ring">
                                                <div className="card-logo-box">
                                                    <img
                                                        src={logoSrc}
                                                        alt="Bentley Systems"
                                                        className="card-logo-img"
                                                    />
                                                </div>
                                            </div>

                                            <div className="card-name">Bentley</div>
                                            <div className="card-tagline">Infrastructure Engineering Software</div>

                                            <div className="card-hint">
                                                <span>Tap to explore</span>
                                                <svg className="hint-arrow" viewBox="0 0 16 16" fill="none">
                                                    <path d="M8 3v10M8 13l-4-4M8 13l4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                                </svg>
                                            </div>
                                        </div>
                                    </div>

                                    {/* ── BACK ── */}
                                    <div className="card-face card-back">
                                        <div className="back-header">
                                            <div className="back-logo-sm">
                                                <img src={logoSmallSrc || logoSrc} alt="Bentley Systems" />
                                            </div>
                                            <span className="back-title">Bentley</span>
                                            <div className="back-status">
                                                <span className="back-status-dot" />
                                                Active
                                            </div>
                                        </div>

                                        <p className="back-desc">
                                            Trusted software that empowers engineers to design, analyse, and manage infrastructure with precision, from concept through construction.
                                        </p>
                                        <div className="back-features">
                                            {[
                                                'Industry-Leading Infrastructure Software',
                                                'iTwin Digital Twin Platform',
                                                'Cloud-Based Project Collaboration',
                                                'Cross-Discipline Design Integration',
                                            ].map((feat) => (
                                                <div className="back-feat" key={feat}>
                                                    <div className="feat-check">
                                                        <svg viewBox="0 0 10 10" fill="none">
                                                            <path d="M2 5l2.5 2.5L8 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                                        </svg>
                                                    </div>
                                                    {feat}
                                                </div>
                                            ))}
                                        </div>
                                        <div className="back-btns">
                                            <button
                                                className="btn-primary"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    window.open('/course/staad-pro', '_blank');
                                                }}
                                            >
                                                Explore Features
                                                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                                    <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                                </svg>
                                            </button>
                                            <button
                                                className="btn-secondary"
                                                onClick={(e) => e.stopPropagation()}
                                            >
                                                Learn More
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </>
    );
};

export default AboutBentley;